import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Paper,
  Stack,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestoreIcon from "@mui/icons-material/Restore";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { useDataProvider, useNotify } from "react-admin";
import { formatDate } from "../../utils/common";
import type { TicketListItem, TicketReport } from "../../models/Ticket";
import type { AppDataProvider } from "../../types/dataProvider";
import {
  canRestoreReportedReply,
  getReportReasonLabel,
  getReportStatusColor,
  getReportStatusLabel,
  getTicketTypeColor,
  getTicketTypeLabel,
} from "./utils";

type LoadState = "loading" | "ready" | "error";

const STATUS_ALL = "all";
const STATUS_PENDING = "pending";
const STATUS_PROCESSED = "processed";
const STATUS_RESTORED = "restored";
const DELETED_ALL = "all";
const DELETED_ONLY = "deleted";
const DELETED_VISIBLE = "visible";

interface ReportRow {
  report: TicketReport;
  ticket: TicketListItem | null;
}

const REPORT_FETCH_CONCURRENCY = 6;

/**
 * 按固定并发数映射异步任务。
 * @param items 待处理列表
 * @param limit 最大并发数
 * @param mapper 异步映射函数
 * @returns 与输入顺序一致的结果列表
 */
const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let cursor = 0;
  const workerCount = Math.min(Math.max(limit, 1), items.length || 1);
  const workers = Array.from({ length: workerCount }, async () => {
    while (cursor < items.length) {
      const current = cursor;
      cursor += 1;
      results[current] = await mapper(items[current], current);
    }
  });
  await Promise.all(workers);
  return results;
};

/**
 * 将举报状态归入筛选分组。
 * @param status 原始状态
 * @returns 筛选分组 key
 */
const getStatusFilterGroup = (status?: string | null): string => {
  const key = String(status ?? "").toLowerCase();
  if (key === "restored" || key === "2") {
    return STATUS_RESTORED;
  }
  if (
    key === "processed"
    || key === "handled"
    || key === "done"
    || key === "1"
    || key === "dismissed"
    || key === "rejected"
    || key === "-1"
  ) {
    return STATUS_PROCESSED;
  }
  return STATUS_PENDING;
};

/**
 * 举报管理页：汇总各工单举报记录，并支持恢复被删除回复。
 */
const ReportManagementPage: React.FC = () => {
  const dataProvider = useDataProvider() as AppDataProvider;
  const notify = useNotify();
  const navigate = useNavigate();

  const [rows, setRows] = React.useState<ReportRow[]>([]);
  const [loadState, setLoadState] = React.useState<LoadState>("loading");
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [refreshing, setRefreshing] = React.useState(false);
  const [loadProgress, setLoadProgress] = React.useState({ loaded: 0, total: 0 });

  const [typeFilter, setTypeFilter] = React.useState<string>("all");
  const [statusFilter, setStatusFilter] = React.useState<string>(STATUS_ALL);
  const [deletedFilter, setDeletedFilter] = React.useState<string>(DELETED_ALL);
  const [searchKeyword, setSearchKeyword] = React.useState<string>("");

  const [pendingRestore, setPendingRestore] = React.useState<ReportRow | null>(null);
  const [restoringKey, setRestoringKey] = React.useState<string | null>(null);

  /**
   * 拉取全部工单后并发获取各工单举报记录。
   * @param mode 初次加载或手动刷新
   */
  const fetchReports = React.useCallback(
    async (mode: "initial" | "refresh") => {
      if (mode === "initial") {
        setLoadState("loading");
      } else {
        setRefreshing(true);
      }
      setErrorMessage("");
      setLoadProgress({ loaded: 0, total: 0 });
      try {
        const ticketResponse = await dataProvider.getTickets();
        const tickets = ticketResponse.data.filter((item) => Boolean(item.id));
        setLoadProgress({ loaded: 0, total: tickets.length });
        let failedCount = 0;
        const grouped = await mapWithConcurrency(
          tickets,
          REPORT_FETCH_CONCURRENCY,
          async (ticket) => {
            try {
              const response = await dataProvider.getTicketReports(ticket.id);
              if (response.data.length > 0) {
                console.log("[举报管理] 工单存在举报记录", {
                  ticketId: ticket.id,
                  ticketTitle: ticket.title,
                  count: response.data.length,
                  reports: response.data,
                });
              }
              return response.data.map((report) => ({ report, ticket }));
            } catch {
              failedCount += 1;
              return [] as ReportRow[];
            } finally {
              setLoadProgress((previous) => ({
                loaded: Math.min(previous.loaded + 1, previous.total || tickets.length),
                total: previous.total || tickets.length,
              }));
            }
          }
        );
        const flattened = grouped.flat().sort((a, b) => {
          const left = a.report.createdAt || "";
          const right = b.report.createdAt || "";
          return right.localeCompare(left);
        });
        setRows(flattened);
        setLoadState("ready");
        if (failedCount > 0) {
          notify(`有 ${failedCount} 个工单的举报记录加载失败`, { type: "warning" });
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "举报记录加载失败";
        setErrorMessage(message);
        setLoadState((previous) => (previous === "ready" ? previous : "error"));
        notify(`举报记录加载失败：${message}`, { type: "error" });
      } finally {
        setRefreshing(false);
      }
    },
    [dataProvider, notify]
  );

  React.useEffect(() => {
    void fetchReports("initial");
  }, [fetchReports]);

  const handleRefresh = () => {
    void fetchReports("refresh");
  };

  /**
   * 打开对应沟通工单详情。
   * @param ticketId 工单 ID
   */
  const handleOpenTicket = (ticketId: string) => {
    if (!ticketId) {
      return;
    }
    navigate(`/communication/${ticketId}`);
  };

  /**
   * 点击「恢复回复」时打开确认弹窗（不直接调用恢复接口）。
   * @param row 当前举报行
   */
  const handleRestoreRequest = (row: ReportRow) => {
    if (restoringKey) {
      return;
    }
    setPendingRestore(row);
  };

  /**
   * 关闭恢复确认弹窗。
   */
  const handleRestoreCancel = () => {
    if (restoringKey) {
      return;
    }
    setPendingRestore(null);
  };

  /**
   * 在确认弹窗中点击确认后，执行恢复被举报回复。
   */
  const handleRestoreConfirm = async () => {
    if (!pendingRestore) {
      return;
    }
    const ticketId = pendingRestore.report.ticketId || pendingRestore.ticket?.id || "";
    const replyId = pendingRestore.report.replyId || "";
    if (!ticketId || !replyId) {
      notify("缺少工单或回复 ID，无法恢复", { type: "warning" });
      return;
    }
    const restoreKey = `${ticketId}:${replyId}:${pendingRestore.report.id}`;
    setRestoringKey(restoreKey);
    try {
      await dataProvider.restoreTicketReply({
        ticketId,
        replyId,
      });
      setRows((previous) =>
        previous.map((item) => {
          const sameReply =
            (item.report.ticketId || item.ticket?.id) === ticketId
            && item.report.replyId === replyId;
          if (!sameReply) {
            return item;
          }
          return {
            ...item,
            report: {
              ...item.report,
              ticketId: item.report.ticketId || ticketId,
              status: "restored",
              replyDeleted: false,
              reply: item.report.reply
                ? { ...item.report.reply, isDeleted: false }
                : item.report.reply,
            },
          };
        })
      );
      setPendingRestore(null);
      notify("已恢复回复", { type: "success" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "恢复失败";
      notify(`恢复失败：${message}`, { type: "error" });
    } finally {
      setRestoringKey(null);
    }
  };

  const typeOptions = React.useMemo(() => {
    const seen = new Set<string>();
    const options: Array<{ value: string; label: string }> = [
      { value: "all", label: "全部类型" },
    ];
    rows.forEach((row) => {
      const raw = row.ticket?.type ?? "";
      const key = raw === "" ? "客服" : String(raw);
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      options.push({ value: key, label: getTicketTypeLabel(key) });
    });
    return options;
  }, [rows]);

  const filteredRows = React.useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    return rows.filter((row) => {
      if (typeFilter !== "all") {
        const raw = row.ticket?.type ?? "";
        const key = raw === "" ? "客服" : String(raw);
        if (key !== typeFilter) {
          return false;
        }
      }
      if (statusFilter !== STATUS_ALL) {
        if (getStatusFilterGroup(row.report.status) !== statusFilter) {
          return false;
        }
      }
      if (deletedFilter === DELETED_ONLY && !row.report.replyDeleted) {
        return false;
      }
      if (deletedFilter === DELETED_VISIBLE && row.report.replyDeleted) {
        return false;
      }
      if (keyword) {
        const haystack = [
          row.ticket?.title,
          row.ticket?.id,
          row.report.reason,
          row.report.reporterName,
          row.report.reporterId,
          row.report.replyId,
          row.report.reply?.content,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(keyword)) {
          return false;
        }
      }
      return true;
    });
  }, [rows, typeFilter, statusFilter, deletedFilter, searchKeyword]);

  const summary = React.useMemo(() => {
    const total = rows.length;
    const pending = rows.filter(
      (item) => getStatusFilterGroup(item.report.status) === STATUS_PENDING
    ).length;
    const deleted = rows.filter((item) => item.report.replyDeleted).length;
    return { total, pending, deleted };
  }, [rows]);

  const resetFilters = () => {
    setTypeFilter("all");
    setStatusFilter(STATUS_ALL);
    setDeletedFilter(DELETED_ALL);
    setSearchKeyword("");
  };

  const renderList = () => {
    if (loadState === "loading") {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={240}
          gap={2}
        >
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            {loadProgress.total > 0
              ? `正在加载举报记录（${loadProgress.loaded}/${loadProgress.total}）`
              : "正在加载举报记录..."}
          </Typography>
        </Box>
      );
    }

    if (loadState === "error" && rows.length === 0) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              重试
            </Button>
          }
        >
          {errorMessage || "举报记录加载失败"}
        </Alert>
      );
    }

    if (rows.length === 0) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={240}
          gap={1}
        >
          <Typography variant="body1" color="text.secondary">
            暂无举报记录
          </Typography>
        </Box>
      );
    }

    if (filteredRows.length === 0) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={240}
          gap={1}
        >
          <Typography variant="body1" color="text.secondary">
            没有匹配的举报记录
          </Typography>
          <Button size="small" onClick={resetFilters}>
            清空筛选
          </Button>
        </Box>
      );
    }

    return (
      <List disablePadding>
        {filteredRows.map((row) => {
          const { report, ticket } = row;
          const restoreKey = `${report.ticketId}:${report.replyId}:${report.id}`;
          const restoring = restoringKey === restoreKey;
          const replyContent = report.reply?.content?.trim() || "暂无回复内容";
          const canRestore =
            canRestoreReportedReply(report.status)
            && Boolean((report.ticketId || ticket?.id) && report.replyId);
          return (
            <React.Fragment key={report.id || restoreKey}>
              <ListItem
                alignItems="flex-start"
                sx={{
                  px: 2,
                  py: 1.5,
                  display: "block",
                }}
              >
                <Stack spacing={0.75} sx={{ width: "100%" }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Chip
                      size="small"
                      color={getTicketTypeColor(ticket?.type)}
                      label={getTicketTypeLabel(ticket?.type)}
                      sx={{ height: 22 }}
                    />
                    <Chip
                      size="small"
                      color={getReportStatusColor(report.status)}
                      label={getReportStatusLabel(report.status)}
                      sx={{ height: 22 }}
                    />
                    {report.replyDeleted ? (
                      <Chip
                        size="small"
                        color="error"
                        variant="outlined"
                        label="回复已删除"
                        sx={{ height: 22 }}
                      />
                    ) : null}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {ticket?.title || "沟通工单"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flexShrink: 0 }}
                    >
                      {report.ticketId || ticket?.id || "-"}
                    </Typography>
                  </Stack>

                  <Typography variant="body2">
                    举报原因：{getReportReasonLabel(report.reason)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      wordBreak: "break-all",
                    }}
                  >
                    被举报内容：{replyContent}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ color: "text.secondary" }}
                  >
                    <Typography variant="caption">
                      举报人：{report.reporterName || report.reporterId || "-"}
                    </Typography>
                    <Typography variant="caption">
                      回复 ID：{report.replyId || "-"}
                    </Typography>
                    <Typography variant="caption">
                      举报时间：{report.createdAt ? formatDate(report.createdAt) : "-"}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleOpenTicket(report.ticketId || ticket?.id || "")}
                      disabled={!report.ticketId && !ticket?.id}
                      sx={{ height: 28 }}
                    >
                      查看工单
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={
                        restoring ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <RestoreIcon sx={{ fontSize: 14 }} />
                        )
                      }
                      onClick={() => handleRestoreRequest(row)}
                      disabled={!canRestore || Boolean(restoringKey)}
                      sx={{ height: 28 }}
                    >
                      {restoring ? "恢复中" : "恢复回复"}
                    </Button>
                  </Stack>
                </Stack>
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="baseline">
          <Typography variant="h6" fontWeight={700}>
            举报管理
          </Typography>
          <Typography variant="body2" color="text.secondary">
            共 {summary.total} 条举报 · {summary.pending} 条待处理 · {summary.deleted} 条回复已删除
          </Typography>
        </Stack>
        <Tooltip title="刷新">
          <span>
            <IconButton size="small" onClick={handleRefresh} disabled={refreshing || loadState === "loading"}>
              {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {errorMessage && rows.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          数据刷新失败：{errorMessage}
        </Alert>
      ) : null}

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} lg={8.5} xl={9}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              minHeight: 480,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 2,
                py: 1.25,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2">
                举报列表（{filteredRows.length}）
              </Typography>
            </Stack>
            {renderList()}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={3.5} xl={3}>
          <Box
            sx={{
              position: { xs: "static", lg: "sticky" },
              top: { lg: 16 },
            }}
          >
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  px: 2,
                  py: 1.25,
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <FilterAltIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">筛选条件</Typography>
              </Stack>

              <Stack spacing={1.5} sx={{ p: 2 }}>
                <MuiTextField
                  select
                  size="small"
                  label="工单类型"
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                >
                  {typeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </MuiTextField>

                <MuiTextField
                  select
                  size="small"
                  label="处理状态"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <MenuItem value={STATUS_ALL}>全部</MenuItem>
                  <MenuItem value={STATUS_PENDING}>待处理</MenuItem>
                  <MenuItem value={STATUS_PROCESSED}>已处理</MenuItem>
                  <MenuItem value={STATUS_RESTORED}>已恢复</MenuItem>
                </MuiTextField>

                <MuiTextField
                  select
                  size="small"
                  label="回复状态"
                  value={deletedFilter}
                  onChange={(event) => setDeletedFilter(event.target.value)}
                >
                  <MenuItem value={DELETED_ALL}>全部</MenuItem>
                  <MenuItem value={DELETED_ONLY}>已删除</MenuItem>
                  <MenuItem value={DELETED_VISIBLE}>未删除</MenuItem>
                </MuiTextField>

                <MuiTextField
                  size="small"
                  label="关键字"
                  placeholder="标题、原因、回复或举报人"
                  value={searchKeyword}
                  onChange={(event) => setSearchKeyword(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.disabled" }}
                      />
                    ),
                  }}
                />

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button size="small" variant="text" onClick={resetFilters}>
                    重置
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={Boolean(pendingRestore)}
        onClose={handleRestoreCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>确认恢复回复</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              确认恢复该条被举报回复？恢复后将重新出现在对应工单会话中。
            </Typography>
            {pendingRestore ? (
              <>
                <Typography variant="body2">
                  工单：{pendingRestore.ticket?.title || pendingRestore.report.ticketId || pendingRestore.ticket?.id || "-"}
                </Typography>
                <Typography variant="body2">
                  回复 ID：{pendingRestore.report.replyId || "-"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-all",
                  }}
                >
                  内容：{pendingRestore.report.reply?.content?.trim() || "暂无回复内容"}
                </Typography>
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleRestoreCancel} disabled={Boolean(restoringKey)}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleRestoreConfirm}
            disabled={Boolean(restoringKey)}
          >
            {restoringKey ? "恢复中" : "确认恢复"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportManagementPage;
